/**
 * Service for managing file labels and annotations.
 */
import supabase from './supabaseClient';
import { MarkedRange, ImageAnnotation } from '../types/training';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

export interface FileLabel {
  id: string;
  user_id: string;
  datalabel_id?: string | null;
  data_file_id: string;
  file_content_hash?: string | null;
  annotations: ImageAnnotation[];
  marks: MarkedRange[];
  labels: any[];
  metadata: any;
  status: 'pending' | 'labeled' | 'reviewed';
  created_at?: string;
  updated_at?: string;
}

export interface SaveFileLabelRequest {
  data_file_id: string;
  annotations?: ImageAnnotation[];
  marks?: MarkedRange[];
  labels?: any[];
  metadata?: any;
  datalabel_id?: string;
  file_content_hash?: string;
  status?: 'pending' | 'labeled' | 'reviewed';
}

class FileLabelService {
  /**
   * Get authentication headers.
   */
  private async getAuthHeaders(): Promise<HeadersInit> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Not authenticated');
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    };
  }

  /**
   * List file labels with optional filters.
   */
  async listFileLabels(params?: {
    datalabel_id?: string;
    data_file_id?: string;
    status?: 'pending' | 'labeled' | 'reviewed';
  }): Promise<FileLabel[]> {
    const headers = await this.getAuthHeaders();
    const queryParams = new URLSearchParams();
    
    if (params?.datalabel_id) {
      queryParams.append('datalabel_id', params.datalabel_id);
    }
    if (params?.data_file_id) {
      queryParams.append('data_file_id', params.data_file_id);
    }
    if (params?.status) {
      queryParams.append('status', params.status);
    }

    const url = `${BACKEND_URL}/api/storage/file-labels${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to list file labels');
    }

    const data = await response.json();
    return data.file_labels || [];
  }

  /**
   * Get a specific file label by ID.
   */
  async getFileLabel(fileLabelId: string): Promise<FileLabel> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/storage/file-labels/${fileLabelId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get file label');
    }

    return response.json();
  }

  /**
   * Get file label by data file ID.
   */
  async getFileLabelByDataFile(dataFileId: string): Promise<FileLabel | null> {
    const labels = await this.listFileLabels({ data_file_id: dataFileId });
    return labels.length > 0 ? labels[0] : null;
  }

  /**
   * Save or update a file label.
   */
  async saveFileLabel(request: SaveFileLabelRequest): Promise<FileLabel> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/storage/file-labels`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to save file label');
    }

    const result = await response.json();
    // If it was an update, fetch the full label
    if (result.file_label_id) {
      return this.getFileLabel(result.file_label_id);
    }
    
    return result;
  }

  /**
   * Delete a file label.
   */
  async deleteFileLabel(fileLabelId: string): Promise<void> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/storage/file-labels/${fileLabelId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete file label');
    }
  }

  /**
   * Get file content URL for preview.
   */
  async getFileContentUrl(dataFileId: string): Promise<string> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/storage/data/${dataFileId}/content`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get file content URL');
    }

    const data = await response.json();
    return data.signed_url;
  }
}

export const fileLabelService = new FileLabelService();
export default fileLabelService;

